import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Campaign } from "schemas/dashboard";

type Choice = "pause" | "archive";

interface Props {
  campaign: Campaign | null;
  open: boolean;
  loading: boolean;
  handleClose: () => void;
  handleConfirm: (pauseFirst: boolean) => void;
}

interface ChoiceRowProps {
  recommended?: boolean;
  selected: boolean;
  icon: string;
  title: string;
  badge?: string;
  description: string;
  onClick: () => void;
}

export default function ArchiveCampaignDialog({
  campaign,
  open,
  loading,
  handleClose,
  handleConfirm,
}: Props) {
  const [choice, setChoice] = useState<Choice>("pause");

  useEffect(() => {
    if (open) setChoice("pause");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(20,30,22,0.34)" } }}
      PaperProps={{
        sx: {
          width: 460,
          maxWidth: "calc(100% - 48px)",
          borderRadius: "18px",
          p: "24px 24px 18px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.24)",
        },
      }}
    >
      <Box
        sx={{
          width: 46,
          height: 46,
          borderRadius: "12px",
          backgroundColor: "#F3EEEA",
          color: "#776B5D",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          mb: "14px",
        }}
      >
        <Box component="i" className="fa-regular fa-box-archive" />
      </Box>

      <Typography
        sx={{ fontWeight: 700, fontSize: 19, color: "#151e16", mb: "6px" }}
      >
        Archive “{campaign?.name}”?
      </Typography>
      <Typography
        sx={{ fontSize: 13.5, color: "#776B5D", lineHeight: 1.55, mb: "18px" }}
      >
        This campaign may have scheduled pitches. How do you want to archive it?
      </Typography>

      <ChoiceRow
        recommended
        selected={choice === "pause"}
        icon="fa-regular fa-pause"
        title="Pause & archive"
        badge="Recommended"
        description="Stops all scheduled pitches, then moves it to Archived. Nothing else will send."
        onClick={() => setChoice("pause")}
      />
      <ChoiceRow
        selected={choice === "archive"}
        icon="fa-regular fa-box-archive"
        title="Archive only"
        description="Hides it from your list but keeps it live — scheduled pitches will still send on time."
        onClick={() => setChoice("archive")}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.25,
          mt: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: "none", color: "#776B5D" }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={() => handleConfirm(choice === "pause")}
          startIcon={
            <Box component="i" className="fa-regular fa-box-archive" />
          }
          sx={{
            textTransform: "none",
            backgroundColor: "#165C4E",
            "&:hover": { backgroundColor: "#014638" },
          }}
        >
          {choice === "pause" ? "Pause & archive" : "Archive only"}
        </LoadingButton>
      </Box>
    </Dialog>
  );
}

function ChoiceRow({
  recommended,
  selected,
  icon,
  title,
  badge,
  description,
  onClick,
}: ChoiceRowProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        width: "100%",
        textAlign: "left",
        border: `1.5px solid ${selected ? "#014638" : "#e0e0e0"}`,
        borderRadius: "13px",
        p: "14px 16px",
        mb: "10px",
        backgroundColor: "#fff",
        cursor: "pointer",
        boxShadow: selected ? "0 0 0 3px rgba(1,70,56,0.08)" : "none",
        transition: "border-color .15s, box-shadow .15s",
        "&:hover": { borderColor: selected ? "#014638" : "#afbfb1" },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "9px",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
          backgroundColor: recommended ? "#DBEFDD" : "#f7f6f0",
          color: recommended ? "#165C4E" : "#776B5D",
        }}
      >
        <Box component="i" className={icon} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            fontSize: 14,
            color: "#151e16",
          }}
        >
          {title}
          {badge && (
            <Box
              component="span"
              sx={{
                fontSize: 9,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontWeight: 600,
                backgroundColor: "#f2f3fe",
                color: "#8084FF",
                px: "7px",
                py: "2px",
                borderRadius: 999,
              }}
            >
              {badge}
            </Box>
          )}
        </Box>
        <Typography
          sx={{ fontSize: 12.5, color: "#776B5D", mt: "3px", lineHeight: 1.45 }}
        >
          {description}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          flexShrink: 0,
          alignSelf: "center",
          border: `2px solid ${selected ? "#014638" : "#e0e0e0"}`,
          backgroundColor: selected ? "#014638" : "transparent",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
        }}
      >
        {selected && <Box component="i" className="fa-solid fa-check" />}
      </Box>
    </Box>
  );
}
